"use client";

type CloudSource = "dropbox" | "google-drive" | "onedrive";

export interface ImportedVideo {
	file: File;
	source: CloudSource | "url" | "device";
	sourceLabel: string;
}

export interface VideoImportResult {
	files: ImportedVideo[];
	failedCount: number;
	cancelled: boolean;
}

interface DropboxFile {
	name: string;
	link: string;
	bytes: number;
	isDir: boolean;
}

interface DropboxApi {
	choose(options: {
		success: (files: DropboxFile[]) => void;
		cancel: () => void;
		linkType: "direct";
		multiselect: boolean;
		extensions: string[];
		folderselect: false;
	}): void;
}

interface GooglePickerDocument {
	id: string;
	name: string;
	mimeType?: string;
	sizeBytes?: number;
}

interface GooglePickerResponse {
	action?: string;
	docs?: GooglePickerDocument[];
}

interface GoogleTokenResponse {
	access_token?: string;
	error?: string;
	error_description?: string;
}

interface GoogleTokenClient {
	requestAccessToken(options?: { prompt?: string }): void;
}

interface GoogleApi {
	accounts: {
		oauth2: {
			initTokenClient(options: {
				client_id: string;
				scope: string;
				callback: (response: GoogleTokenResponse) => void;
			}): GoogleTokenClient;
		};
	};
	picker: {
		Action: { PICKED: string; CANCEL: string };
		DocsView: new (viewId: string) => {
			setIncludeFolders(value: boolean): unknown;
			setSelectFolderEnabled(value: boolean): unknown;
			setMimeTypes(value: string): unknown;
			setMode(value: string): unknown;
		};
		DocsViewMode: { LIST: string };
		Feature: { MULTISELECT_ENABLED: string };
		PickerBuilder: new () => {
			addView(
				view: unknown,
			): GoogleApi["picker"]["PickerBuilder"] extends new () => infer T
				? T
				: never;
			enableFeature(
				feature: string,
			): GoogleApi["picker"]["PickerBuilder"] extends new () => infer T
				? T
				: never;
			setOAuthToken(
				token: string,
			): GoogleApi["picker"]["PickerBuilder"] extends new () => infer T
				? T
				: never;
			setDeveloperKey(
				key: string,
			): GoogleApi["picker"]["PickerBuilder"] extends new () => infer T
				? T
				: never;
			setAppId(
				appId: string,
			): GoogleApi["picker"]["PickerBuilder"] extends new () => infer T
				? T
				: never;
			setOrigin(
				origin: string,
			): GoogleApi["picker"]["PickerBuilder"] extends new () => infer T
				? T
				: never;
			setCallback(
				callback: (data: GooglePickerResponse) => void,
			): GoogleApi["picker"]["PickerBuilder"] extends new () => infer T
				? T
				: never;
			build(): { setVisible(value: boolean): void };
		};
		ViewId: { DOCS: string };
	};
}

interface GoogleApiLoader {
	load(
		name: string,
		options: { callback: () => void; onerror: () => void },
	): void;
}

interface OneDrivePickerItem {
	id: string;
	name?: string;
	size?: number;
	file?: { mimeType?: string };
	parentReference?: { driveId?: string };
	"@sharePoint.endpoint"?: string;
}

interface OneDriveCommandMessage {
	type: "command";
	id: string;
	data: { command: string; items?: OneDrivePickerItem[] };
}

const VIDEO_EXTENSIONS = [
	"mp4",
	"mov",
	"mkv",
	"m4v",
	"webm",
	"avi",
	"flv",
	"wmv",
	"3gp",
	"3g2",
	"ogv",
	"mpeg",
	"mpg",
	"ts",
	"mts",
	"m2ts",
];

const GOOGLE_VIDEO_MIME_TYPES = [
	"video/mp4",
	"video/quicktime",
	"video/x-matroska",
	"video/webm",
	"video/x-msvideo",
	"video/x-flv",
	"video/x-ms-wmv",
	"video/3gpp",
	"video/ogg",
	"video/mpeg",
	"video/mp2t",
].join(",");

const DROPBOX_APP_KEY = process.env.NEXT_PUBLIC_DROPBOX_APP_KEY ?? "";
const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_DRIVE_CLIENT_ID ?? "";
const GOOGLE_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_DRIVE_API_KEY ?? "";
const GOOGLE_APP_ID = process.env.NEXT_PUBLIC_GOOGLE_DRIVE_APP_ID ?? "";
const ONEDRIVE_CLIENT_ID = process.env.NEXT_PUBLIC_ONEDRIVE_CLIENT_ID ?? "";

const scriptPromises = new Map<string, Promise<void>>();

function abortError(): DOMException {
	return new DOMException("The import was cancelled.", "AbortError");
}

function throwIfAborted(signal?: AbortSignal): void {
	if (signal?.aborted) throw abortError();
}

function loadScript({
	id,
	src,
	attributes,
}: {
	id: string;
	src: string;
	attributes?: Record<string, string>;
}): Promise<void> {
	const pending = scriptPromises.get(id);
	if (pending) return pending;

	const promise = new Promise<void>((resolve, reject) => {
		const candidate = document.getElementById(id);
		const existing =
			candidate instanceof HTMLScriptElement ? candidate : undefined;
		if (existing?.dataset.loaded === "true") {
			resolve();
			return;
		}

		const script = existing ?? document.createElement("script");
		script.id = id;
		script.src = src;
		script.async = true;
		for (const [name, value] of Object.entries(attributes ?? {})) {
			script.setAttribute(name, value);
		}
		script.addEventListener(
			"load",
			() => {
				script.dataset.loaded = "true";
				resolve();
			},
			{ once: true },
		);
		script.addEventListener(
			"error",
			() => {
				scriptPromises.delete(id);
				reject(new Error("The provider could not be loaded. Try again."));
			},
			{ once: true },
		);
		if (!existing) document.head.appendChild(script);
	});

	scriptPromises.set(id, promise);
	return promise;
}

function filenameFromResponse({
	response,
	fallback,
}: {
	response: Response;
	fallback: string;
}): string {
	const disposition = response.headers.get("content-disposition");
	const utf8Match = disposition?.match(/filename\*=UTF-8''([^;]+)/i);
	const plainMatch = disposition?.match(/filename="?([^";]+)"?/i);
	const candidate = utf8Match?.[1] ?? plainMatch?.[1];
	if (!candidate) return fallback;
	try {
		return decodeURIComponent(candidate);
	} catch {
		return candidate;
	}
}

function isSupportedVideo({
	name,
	mimeType,
}: {
	name: string;
	mimeType: string;
}): boolean {
	if (mimeType.toLowerCase().startsWith("video/")) return true;
	const extension = name.split(".").pop()?.toLowerCase();
	return extension ? VIDEO_EXTENSIONS.includes(extension) : false;
}

async function fetchVideo({
	url,
	name,
	headers,
	signal,
}: {
	url: string;
	name: string;
	headers?: HeadersInit;
	signal?: AbortSignal;
}): Promise<File> {
	throwIfAborted(signal);
	let response: Response;
	try {
		response = await fetch(url, {
			headers,
			signal,
			credentials: "omit",
			redirect: "follow",
		});
	} catch (error) {
		if (signal?.aborted) throw abortError();
		throw new Error(
			"The file could not be read in this browser. Check access and cross-origin permissions.",
			{ cause: error },
		);
	}

	if (!response.ok) {
		throw new Error(`The provider returned HTTP ${response.status}.`);
	}

	const resolvedName = filenameFromResponse({ response, fallback: name });
	const mimeType = response.headers.get("content-type")?.split(";")[0] ?? "";
	if (!isSupportedVideo({ name: resolvedName, mimeType })) {
		throw new Error("The selected file is not a supported video.");
	}

	const blob = await response.blob();
	throwIfAborted(signal);
	return new File([blob], resolvedName, {
		type: mimeType || blob.type || "application/octet-stream",
		lastModified: Date.now(),
	});
}

async function settleVideoImports(
	imports: Array<Promise<ImportedVideo>>,
): Promise<Pick<VideoImportResult, "files" | "failedCount">> {
	const results = await Promise.allSettled(imports);
	return {
		files: results.flatMap((result) =>
			result.status === "fulfilled" ? [result.value] : [],
		),
		failedCount: results.filter((result) => result.status === "rejected")
			.length,
	};
}

async function loadGooglePicker(): Promise<void> {
	await Promise.all([
		loadScript({
			id: "editing-video-google-api",
			src: "https://apis.google.com/js/api.js",
		}),
		loadScript({
			id: "editing-video-google-identity",
			src: "https://accounts.google.com/gsi/client",
		}),
	]);

	const gapi = (window as Window & { gapi?: GoogleApiLoader }).gapi;
	if (!gapi) throw new Error("Google Drive could not be initialized.");

	await new Promise<void>((resolve, reject) => {
		gapi.load("picker", {
			callback: resolve,
			onerror: () => reject(new Error("Google Drive Picker could not load.")),
		});
	});
}

export function preloadCloudSourceSdks(): void {
	if (DROPBOX_APP_KEY) {
		void loadScript({
			id: "dropboxjs",
			src: "https://www.dropbox.com/static/api/2/dropins.js",
			attributes: { "data-app-key": DROPBOX_APP_KEY },
		}).catch(() => undefined);
	}
	if (GOOGLE_CLIENT_ID && GOOGLE_API_KEY && GOOGLE_APP_ID) {
		void loadGooglePicker().catch(() => undefined);
	}
}

export async function importDropboxVideos({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<VideoImportResult> {
	if (!DROPBOX_APP_KEY) {
		throw new Error(
			"Dropbox is not configured for this site yet. Add the Dropbox app key and allowed domain.",
		);
	}

	await loadScript({
		id: "dropboxjs",
		src: "https://www.dropbox.com/static/api/2/dropins.js",
		attributes: { "data-app-key": DROPBOX_APP_KEY },
	});
	throwIfAborted(signal);

	const dropbox = (window as Window & { Dropbox?: DropboxApi }).Dropbox;
	if (!dropbox) throw new Error("Dropbox could not be initialized.");

	return new Promise<VideoImportResult>((resolve, reject) => {
		dropbox.choose({
			linkType: "direct",
			multiselect: true,
			extensions: VIDEO_EXTENSIONS.map((extension) => `.${extension}`),
			folderselect: false,
			cancel: () => resolve({ files: [], failedCount: 0, cancelled: true }),
			success: (selectedFiles) => {
				void settleVideoImports(
					selectedFiles
						.filter((item) => !item.isDir)
						.map(async (item) => ({
							file: await fetchVideo({
								url: item.link,
								name: item.name,
								signal,
							}),
							source: "dropbox" as const,
							sourceLabel: "Dropbox",
						})),
				).then((result) => resolve({ ...result, cancelled: false }), reject);
			},
		});
	});
}

async function requestGoogleToken(google: GoogleApi): Promise<string> {
	return new Promise<string>((resolve, reject) => {
		const tokenClient = google.accounts.oauth2.initTokenClient({
			client_id: GOOGLE_CLIENT_ID,
			scope: "https://www.googleapis.com/auth/drive.file",
			callback: (response) => {
				if (response.error || !response.access_token) {
					reject(
						new Error(
							response.error_description ??
								"Google Drive authorization was cancelled or failed.",
						),
					);
					return;
				}
				resolve(response.access_token);
			},
		});
		tokenClient.requestAccessToken({ prompt: "consent" });
	});
}

async function pickGoogleVideos({
	google,
	accessToken,
}: {
	google: GoogleApi;
	accessToken: string;
}): Promise<GooglePickerDocument[] | null> {
	return new Promise((resolve) => {
		const docsView = new google.picker.DocsView(google.picker.ViewId.DOCS);
		docsView.setIncludeFolders(false);
		docsView.setSelectFolderEnabled(false);
		docsView.setMimeTypes(GOOGLE_VIDEO_MIME_TYPES);
		docsView.setMode(google.picker.DocsViewMode.LIST);

		const picker = new google.picker.PickerBuilder()
			.addView(docsView)
			.enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
			.setOAuthToken(accessToken)
			.setDeveloperKey(GOOGLE_API_KEY)
			.setAppId(GOOGLE_APP_ID)
			.setOrigin(window.location.origin)
			.setCallback((data) => {
				if (data.action === google.picker.Action.PICKED) {
					resolve(data.docs ?? []);
				} else if (data.action === google.picker.Action.CANCEL) {
					resolve(null);
				}
			})
			.build();
		picker.setVisible(true);
	});
}

export async function importGoogleDriveVideos({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<VideoImportResult> {
	if (!GOOGLE_CLIENT_ID || !GOOGLE_API_KEY || !GOOGLE_APP_ID) {
		throw new Error(
			"Google Drive is not configured for this site yet. Add its client ID, API key, app ID, and allowed origin.",
		);
	}

	await loadGooglePicker();
	throwIfAborted(signal);
	const google = (window as Window & { google?: GoogleApi }).google;
	if (!google?.accounts?.oauth2 || !google.picker) {
		throw new Error("Google Drive could not be initialized.");
	}

	const accessToken = await requestGoogleToken(google);
	throwIfAborted(signal);
	const selectedFiles = await pickGoogleVideos({ google, accessToken });
	if (!selectedFiles) return { files: [], failedCount: 0, cancelled: true };

	const result = await settleVideoImports(
		selectedFiles.map(async (item) => ({
			file: await fetchVideo({
				url: `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(item.id)}?alt=media`,
				name: item.name,
				headers: { Authorization: `Bearer ${accessToken}` },
				signal,
			}),
			source: "google-drive" as const,
			sourceLabel: "Google Drive",
		})),
	);
	return { ...result, cancelled: false };
}

let oneDriveClient:
	| Promise<import("@azure/msal-browser").PublicClientApplication>
	| undefined;

async function getOneDriveClient() {
	if (!oneDriveClient) {
		oneDriveClient = import("@azure/msal-browser").then(
			async ({ PublicClientApplication }) => {
				const client = new PublicClientApplication({
					auth: {
						clientId: ONEDRIVE_CLIENT_ID,
						authority: "https://login.microsoftonline.com/consumers",
						redirectUri: window.location.origin,
					},
					cache: { cacheLocation: "sessionStorage" },
				});
				await client.initialize();
				return client;
			},
		);
	}
	return oneDriveClient;
}

async function getOneDriveToken(): Promise<string> {
	const client = await getOneDriveClient();
	const scopes = ["OneDrive.ReadOnly"];
	const account = client.getActiveAccount() ?? client.getAllAccounts()[0];
	if (account) {
		try {
			const response = await client.acquireTokenSilent({ account, scopes });
			return response.accessToken;
		} catch {
			// The provider requires a fresh user interaction.
		}
	}

	const response = await client.loginPopup({
		scopes,
		prompt: account ? undefined : "select_account",
	});
	client.setActiveAccount(response.account);
	if (!response.accessToken) {
		throw new Error("OneDrive did not return permission to read the file.");
	}
	return response.accessToken;
}

function oneDriveDownloadUrl(item: OneDrivePickerItem): string | null {
	const endpoint = item["@sharePoint.endpoint"]?.replace(/\/$/, "");
	const driveId = item.parentReference?.driveId;
	if (!endpoint || !driveId || !item.id) return null;
	return `${endpoint}/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(item.id)}/content`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

function isOneDrivePickerItem(value: unknown): value is OneDrivePickerItem {
	return isRecord(value) && typeof value.id === "string";
}

function readOneDriveCommand(value: unknown): OneDriveCommandMessage | null {
	if (
		!isRecord(value) ||
		value.type !== "command" ||
		typeof value.id !== "string"
	)
		return null;
	if (!isRecord(value.data) || typeof value.data.command !== "string")
		return null;
	const items = Array.isArray(value.data.items)
		? value.data.items.filter(isOneDrivePickerItem)
		: undefined;
	return {
		type: "command",
		id: value.id,
		data: { command: value.data.command, items },
	};
}

function isOneDriveInitializeMessage({
	value,
	channelId,
}: {
	value: unknown;
	channelId: string;
}): boolean {
	return (
		isRecord(value) &&
		value.type === "initialize" &&
		value.channelId === channelId
	);
}

export async function importOneDriveVideos({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<VideoImportResult> {
	if (!ONEDRIVE_CLIENT_ID) {
		throw new Error(
			"OneDrive is not configured for this site yet. Add its application client ID and allowed redirect URL.",
		);
	}

	const pickerWindow = window.open(
		"",
		`editing-video-onedrive-${crypto.randomUUID()}`,
		"width=1080,height=680,resizable=yes,scrollbars=yes",
	);
	if (!pickerWindow) {
		throw new Error(
			"The OneDrive window was blocked. Allow pop-ups and try again.",
		);
	}

	const channelId = crypto.randomUUID();
	const baseUrl = "https://onedrive.live.com/picker";
	const options = {
		sdk: "8.0",
		entry: { oneDrive: {} },
		authentication: {},
		messaging: { origin: window.location.origin, channelId },
		typesAndSources: { mode: "files", filters: ["video"] },
		selection: { mode: "multiple" },
	};

	return new Promise<VideoImportResult>((resolve, reject) => {
		let settled = false;
		let port: MessagePort | undefined;
		const finish = (callback: () => void) => {
			if (settled) return;
			settled = true;
			window.removeEventListener("message", onWindowMessage);
			signal?.removeEventListener("abort", onAbort);
			window.clearTimeout(timeoutId);
			port?.close();
			if (!pickerWindow.closed) pickerWindow.close();
			callback();
		};
		const fail = (error: unknown) =>
			finish(() =>
				reject(error instanceof Error ? error : new Error(String(error))),
			);
		const cancel = () =>
			finish(() => resolve({ files: [], failedCount: 0, cancelled: true }));
		const onAbort = () => fail(abortError());

		const sendResult = ({ id, data }: { id: string; data: unknown }) => {
			port?.postMessage({ type: "result", id, data });
		};

		const onChannelMessage = (event: MessageEvent) => {
			const eventData: unknown = event.data;
			const payload = readOneDriveCommand(eventData);
			if (!payload) return;
			port?.postMessage({ type: "acknowledge", id: payload.id });

			if (payload.data.command === "authenticate") {
				void getOneDriveToken().then(
					(token) =>
						sendResult({ id: payload.id, data: { result: "token", token } }),
					(error) =>
						sendResult({
							id: payload.id,
							data: {
								result: "error",
								error: {
									code: "unableToObtainToken",
									message:
										error instanceof Error ? error.message : String(error),
								},
							},
						}),
				);
				return;
			}

			if (payload.data.command === "close") {
				sendResult({ id: payload.id, data: { result: "success" } });
				cancel();
				return;
			}

			if (payload.data.command === "pick") {
				const selectedItems = payload.data.items ?? [];
				sendResult({ id: payload.id, data: { result: "success" } });
				void getOneDriveToken()
					.then((token) =>
						settleVideoImports(
							selectedItems.map(async (item) => {
								const url = oneDriveDownloadUrl(item);
								if (!url) {
									throw new Error(
										"OneDrive did not return a readable file link.",
									);
								}
								return {
									file: await fetchVideo({
										url,
										name: item.name ?? "OneDrive video",
										headers: { Authorization: `Bearer ${token}` },
										signal,
									}),
									source: "onedrive" as const,
									sourceLabel: "OneDrive",
								};
							}),
						),
					)
					.then(
						(result) => finish(() => resolve({ ...result, cancelled: false })),
						fail,
					);
			}
		};

		const onWindowMessage = (event: MessageEvent) => {
			if (event.source !== pickerWindow) return;
			const eventData: unknown = event.data;
			if (!isOneDriveInitializeMessage({ value: eventData, channelId })) return;
			port = event.ports[0];
			if (!port) {
				fail(
					new Error("OneDrive could not establish a secure picker session."),
				);
				return;
			}
			port.addEventListener("message", onChannelMessage);
			port.start();
			port.postMessage({ type: "activate" });
		};

		window.addEventListener("message", onWindowMessage);
		signal?.addEventListener("abort", onAbort, { once: true });
		const timeoutId = window.setTimeout(
			() =>
				fail(new Error("OneDrive did not respond. Close it and try again.")),
			5 * 60 * 1000,
		);

		void getOneDriveToken().then((accessToken) => {
			throwIfAborted(signal);
			const query = new URLSearchParams({
				filePicker: JSON.stringify(options),
				locale: "en-us",
			});
			const form = pickerWindow.document.createElement("form");
			form.method = "POST";
			form.action = `${baseUrl}/_layouts/15/FilePicker.aspx?${query}`;
			const tokenInput = pickerWindow.document.createElement("input");
			tokenInput.type = "hidden";
			tokenInput.name = "access_token";
			tokenInput.value = accessToken;
			form.appendChild(tokenInput);
			pickerWindow.document.body.appendChild(form);
			form.submit();
		}, fail);
	});
}

export async function importVideoFromUrl({
	value,
	signal,
}: {
	value: string;
	signal?: AbortSignal;
}): Promise<ImportedVideo> {
	let parsed: URL;
	try {
		parsed = new URL(value.trim());
	} catch {
		throw new Error("Enter a complete public http:// or https:// URL.");
	}
	if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
		throw new Error("Only public http:// or https:// URLs can be imported.");
	}

	const pathName = parsed.pathname.split("/").filter(Boolean).at(-1);
	const fallbackName = pathName ? decodeURIComponent(pathName) : "Remote video";
	return {
		file: await fetchVideo({ url: parsed.href, name: fallbackName, signal }),
		source: "url",
		sourceLabel: parsed.hostname,
	};
}
